import React from "react";
import { PublicHeader, Footer } from "@/components/Layout";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowRight, MessageSquareQuote } from "lucide-react";
import { PricingCards } from "@/components/PricingCards";

const PageWrap = ({title, subtitle, children, testid}) => (
  <div data-testid={testid}>
    <PublicHeader />
    <section className="bg-white border-b border-slate-200">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-4xl sm:text-5xl font-black font-display text-slate-900">{title}</h1>
        {subtitle && <p className="mt-4 text-lg text-slate-600 max-w-2xl">{subtitle}</p>}
      </div>
    </section>
    <section className="py-12 lg:py-16">
      <div className="max-w-4xl mx-auto px-6 prose-doc text-slate-700 leading-relaxed">
        {children}
      </div>
    </section>
    <Footer />
  </div>
);

export function HowItWorks() {
  return (
    <PageWrap testid="page-how-it-works" title="How It Works" subtitle="A guided workflow that turns messy incidents into organized grievance documents.">
      <ol className="space-y-6">
        {[
          ["Create your account","Register in under a minute. Complete your profile with employer, local union, and job classification."],
          ["Start a grievance","Answer plain-language questions about the incident, the supervisor, and what was said."],
          ["Pick a category","Choose from 30+ UPS-specific grievance categories."],
          ["Review suggested articles","The platform recommends contract articles based on your facts. You can search, remove, or add articles."],
          ["Generate the grievance","Get professional grievance wording — editable, using only the facts you entered."],
          ["Add a remedy","Choose from suggested remedies or write your own."],
          ["Add witnesses & evidence","Upload documents or record what evidence exists."],
          ["Review and export","Download a printable PDF, share with your steward, and track the case status."],
        ].map(([t,b], i)=>(
          <li key={i} className="flex gap-4">
            <span className="flex-shrink-0 h-9 w-9 rounded-full bg-red-600 text-white flex items-center justify-center font-bold">{i+1}</span>
            <div><h3 className="font-bold text-slate-900 text-lg font-display">{t}</h3><p className="mt-1">{b}</p></div>
          </li>
        ))}
      </ol>
      <div className="mt-12"><Link to="/register"><Button data-testid="how-cta-btn" className="bg-red-600 hover:bg-red-700 text-white">Start My Grievance</Button></Link></div>
    </PageWrap>
  );
}

export function SupportedContracts() {
  const groups = [
    ["National", [
      "UPS National Master Agreement 2023–2028",
    ]],
    ["Regional / Area Supplements", [
      "Central Region Supplemental Agreement 2023–2028",
      "Southern Region Supplemental Agreement 2023–2028",
      "Atlantic Area Supplemental Agreement 2023–2028",
      "New England Supplemental Agreement 2023–2028",
      "Northern California Supplemental Agreement 2023–2028",
      "Western Region Supplemental Agreement 2023–2028",
      "Upstate/West New York Supplemental Agreement 2023–2028",
      "Western Pennsylvania Supplemental Agreement 2023–2028",
      "Central Pennsylvania Supplemental Agreement 2023–2028",
    ]],
    ["Riders", [
      "Southwest Package Rider 2023–2028",
      "Southwest Sort Rider 2023–2028",
      "Ohio Rider 2023–2028",
      "Michigan Rider 2023–2028",
      "Local 89 Louisville Air Rider 2023–2028",
      "Local 135 Rider 2023–2028",
      "Northern California Sort Rider 2023–2028",
    ]],
    ["Local / Specialized Agreements", [
      "Local 135 Clerical Group Supplement 2023–2028",
      "Local 243 Metro Detroit Supplemental Agreement 2023–2028",
    ]],
  ];
  return (
    <PageWrap testid="page-supported-contracts" title="Supported Contracts" subtitle="FMG combines the UPS National Master Agreement with applicable regional supplements, riders, and local agreements to provide more precise contract guidance.">
      {groups.map(([label, items]) => (
        <div key={label} className="mt-8 first:mt-0">
          <h2 className="font-display font-bold text-slate-900 text-2xl">{label}</h2>
          <ul className="mt-4 space-y-2" data-testid={`supported-contracts-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            {items.map((t) => <li key={t}>• {t}</li>)}
          </ul>
        </div>
      ))}
      <p className="mt-10 text-slate-500 text-sm" data-testid="supported-contracts-note">More agreements are being added as they are reviewed and validated.</p>
    </PageWrap>
  );
}

export function WhyThisExists() {
  const workerStories = [
    { title: "Forgot the witness names", body: "\"By the time I sat down with my steward two weeks later, I couldn't remember who was standing there when the supervisor said it. I knew there were two of them. I just couldn't place their faces to names anymore.\"" },
    { title: "Didn't write the dates down", body: "\"I thought I'd remember. The dates all blur together after a while — which day the write-up happened, which day the supervisor worked, which day I asked for the shop steward. I lost the timeline.\"" },
    { title: "Didn't understand the contract language", body: "\"I knew something was wrong. I couldn't tell you which article. I don't read the National Master for fun. Nobody expects you to.\"" },
    { title: "Lost the evidence", body: "\"I had a picture of the schedule board on my phone. I switched phones six months later and never thought to save it. That picture was the whole case.\"" },
    { title: "Didn't know where to begin", body: "\"I knew I had a grievance. I didn't know what to write, who to give it to, or what to say when I sat down with the steward. So I did nothing until the deadline passed.\"" },
  ];
  const problemSolution = [
    { problem: "I forgot what happened.", solution: "Incident Recorder", solutionBody: "Capture facts, names, times, and voice notes in under a minute — before the details fade." },
    { problem: "I didn't know the contract.", solution: "AI Contract Analysis", solutionBody: "The platform reads your facts and suggests which articles may apply, in plain language." },
    { problem: "I lost my evidence.", solution: "Evidence Management", solutionBody: "Photos, screenshots, warning letters, and witness names all stored with the incident they belong to." },
    { problem: "My grievance wasn't organized.", solution: "Professional Grievance Builder", solutionBody: "A guided flow that turns messy incidents into a clean, printable grievance document." },
  ];
  return (
    <div data-testid="page-why-it-exists">
      <PublicHeader />

      {/* Hero */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-16 lg:py-20">
          <span className="inline-block text-xs font-semibold tracking-widest text-red-700 bg-red-50 border border-red-100 rounded-full px-3 py-1 uppercase">Why We Built This</span>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black font-display text-slate-900 tracking-tight">Every feature exists because someone experienced a problem.</h1>
          <p className="mt-6 text-lg text-slate-700 max-w-2xl">This wasn't built by a software company looking for a market. It was built to solve a problem experienced by real workers.</p>
        </div>
      </section>

      {/* 1. The Problem */}
      <section className="py-14 lg:py-20 bg-slate-50 border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6">
          <p className="text-sm font-semibold tracking-widest text-red-700 uppercase">01 — The Problem</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-black font-display text-slate-900">The grievance process is stacked against memory.</h2>
          <div className="mt-6 space-y-5 text-slate-700 leading-relaxed">
            <p>Every grievance depends on facts. Who said what. What day it happened. Who else was standing there. What the schedule showed. What the write-up looked like before it got shoved in a folder.</p>
            <p>But workers don't get to write it all down in the moment. You finish the shift. You go home. You cool off. You come back the next day and something else happens.</p>
            <p>By the time you sit down with your steward — sometimes a week later, sometimes a month later — the important pieces have already started to slip. Names blur. Dates blend together. The photo of the supervisor working got deleted when the phone filled up. The witness moved to a different building.</p>
            <p>The contract itself doesn't help. It's hundreds of pages. It's written for lawyers, not for warehouse workers or drivers. Nobody expects you to know which article applies to your situation — but somehow, the system expects you to file a grievance that cites the right one anyway.</p>
            <p>And your steward? Your steward is doing their best. They're also probably juggling twenty other cases. They can only work with what you bring them.</p>
          </div>
        </div>
      </section>

      {/* 2. The Story Behind FileMyGrievance */}
      <section className="py-14 lg:py-20 bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6">
          <p className="text-sm font-semibold tracking-widest text-red-700 uppercase">02 — The Story Behind FileMyGrievance</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-black font-display text-slate-900">Built by someone who lived it.</h2>
          <div className="mt-6 space-y-5 text-slate-700 leading-relaxed">
            <p>The founder spent years working in union environments. Long enough to see the same problem play out in a hundred different ways.</p>
            <p>A coworker gets written up for something the supervisor did wrong. They know it. Everyone on the shift knows it. But by the time they sit down to file a grievance, half the details are gone. The witness statement never got written. The photo of the timecard got deleted. The exact words the supervisor used got softened in memory into something more polite than what actually happened.</p>
            <p>He watched people lose grievances they should have won — not because their union failed them, and not because the company was too strong, but because the paperwork walking into the room wasn't as strong as the story that actually happened at work.</p>
            <p>He watched people try to read the contract for the first time only after something went wrong. He watched stewards do heroic work with incomplete information. He watched deadlines slip because nobody knew the clock had already started.</p>
            <p>After going through his own grievance process — and seeing firsthand how much of the outcome depended on documentation he wished he'd captured earlier — he realized workers needed better tools. Not lawyers. Not a replacement for the union. Just a practical way to preserve facts while they're fresh, understand what the contract says in plain language, and walk into the union hall already organized.</p>
            <p>That's what this platform is.</p>
          </div>
        </div>
      </section>

      {/* 3. What Other Workers Told Us */}
      <section className="py-14 lg:py-20 bg-slate-50 border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-sm font-semibold tracking-widest text-red-700 uppercase">03 — What Other Workers Told Us</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-black font-display text-slate-900">The same gaps, over and over.</h2>
          <p className="mt-4 text-slate-700 max-w-2xl">Anonymous stories from workers who lived through the grievance process. Names withheld. Details lightly edited for privacy. New stories will be added over time.</p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="worker-stories">
            {workerStories.map((s, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-md p-6" data-testid={`worker-story-${i}`}>
                <MessageSquareQuote className="h-5 w-5 text-red-600"/>
                <h3 className="mt-3 font-display font-bold text-slate-900">{s.title}</h3>
                <p className="mt-3 text-slate-700 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. What We Learned */}
      <section className="py-14 lg:py-20 bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6">
          <p className="text-sm font-semibold tracking-widest text-red-700 uppercase">04 — What We Learned</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-black font-display text-slate-900">Five things that show up in almost every case.</h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-5 gap-3">
            {[
              "Documentation matters.",
              "Facts fade quickly.",
              "Evidence disappears.",
              "Preparation helps.",
              "Organization wins.",
            ].map((lesson, i) => (
              <div key={i} className="bg-slate-900 text-white rounded-md p-5 text-center" data-testid={`lesson-${i}`}>
                <div className="text-red-400 text-xs font-semibold tracking-widest">0{i+1}</div>
                <p className="mt-2 font-display font-bold leading-snug">{lesson}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Why We Built FileMyGrievance */}
      <section className="py-14 lg:py-20 bg-slate-50 border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-sm font-semibold tracking-widest text-red-700 uppercase">05 — Why We Built FileMyGrievance</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-black font-display text-slate-900">Every feature is a response to a real problem.</h2>
          <div className="mt-10 space-y-4">
            {problemSolution.map((ps, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start bg-white border border-slate-200 rounded-md p-6" data-testid={`problem-solution-${i}`}>
                <div className="md:col-span-5">
                  <p className="text-xs font-semibold tracking-widest text-slate-500 uppercase">Problem</p>
                  <p className="mt-2 font-display font-bold text-slate-900 text-lg leading-snug">&ldquo;{ps.problem}&rdquo;</p>
                </div>
                <div className="md:col-span-1 flex md:justify-center">
                  <ArrowRight className="h-6 w-6 text-red-600 mt-1"/>
                </div>
                <div className="md:col-span-6">
                  <p className="text-xs font-semibold tracking-widest text-red-700 uppercase">Solution</p>
                  <p className="mt-2 font-display font-bold text-slate-900 text-lg leading-snug">{ps.solution}</p>
                  <p className="mt-2 text-sm text-slate-600">{ps.solutionBody}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing */}
      <section className="py-16 lg:py-20 bg-slate-900 text-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <MessageSquareQuote className="h-8 w-8 mx-auto text-red-400"/>
          <p className="mt-6 text-2xl sm:text-3xl font-display font-black leading-snug" data-testid="why-closing-quote">
            "Every feature in FileMyGrievance exists because someone experienced a problem that needed a better solution."
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register">
              <Button data-testid="why-closing-start-btn" size="lg" className="bg-red-600 hover:bg-red-700 text-white h-12 px-8">Start Free</Button>
            </Link>
            <Link to="/register">
              <Button data-testid="why-closing-build-btn" size="lg" variant="outline" className="border-white text-white hover:bg-slate-800 h-12 px-8">Build My First Grievance</Button>
            </Link>
          </div>
          <p className="mt-6 text-xs text-slate-400 max-w-md mx-auto">Independent document-preparation platform. Not a union. Not a law firm. Not legal advice.</p>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export function Pricing() {
  return (
    <div data-testid="page-plans">
      <PublicHeader />
      {/* Section intro */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20 text-center">
          <span className="inline-block text-xs font-semibold tracking-widest text-red-700 bg-red-50 border border-red-100 rounded-full px-3 py-1 uppercase" data-testid="plans-eyebrow">Plans</span>
          <h1 className="mt-4 text-4xl sm:text-5xl font-black font-display text-slate-900" data-testid="plans-title">Choose the plan that's right for you.</h1>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed" data-testid="plans-subtitle">
            Start documenting workplace events today at no cost. Upgrade only when you're ready to create additional grievances and unlock advanced AI-powered workplace tools.
          </p>
        </div>
      </section>

      {/* Plan cards */}
      <section className="py-12 lg:py-16 bg-slate-50 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6">
          <PricingCards mode="public" />

          {/* Bottom reassurance */}
          <section className="mt-14 bg-white border border-slate-200 rounded-md p-10 text-center shadow-sm" data-testid="reassurance-section">
            <h2 className="text-2xl sm:text-3xl font-black font-display text-slate-900">You don't have to decide today.</h2>
            <p className="mt-4 text-slate-700 max-w-2xl mx-auto leading-relaxed">
              Your Workplace Timeline, notes, evidence, and first grievance remain available on the free plan.
            </p>
            <p className="mt-3 text-slate-700 max-w-2xl mx-auto leading-relaxed">
              Upgrade only when Premium provides additional value for your situation.
            </p>
          </section>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export function FAQ() {
  const items = [
    ["Is this a union?","No. FileMyGrievance.com is an independent document-preparation platform. We are not a union."],
    ["Does this replace my union steward?","No. This tool helps you prepare and organize. Your steward, business agent, and local union remain your representatives."],
    ["Does this provide legal advice?","No. We do not provide legal advice. Nothing here should be treated as such."],
    ["Which contracts are supported?","The UPS Teamsters National Master Agreement and selected UPS supplements/riders. More contracts are on the way."],
    ["Can I save my grievance?","Yes. Grievances autosave as drafts and can be revisited any time."],
    ["Can I print the grievance?","Yes. Generate a professional PDF for printing or sharing."],
    ["Is my information private?","Your data is stored in an encrypted database and only visible to you and users you explicitly share cases with."],
    ["What happens after I create a grievance?","You can share it with a steward, mark it as filed, and track status updates and timeline events."],
    ["Can a steward review my case?","Yes. If you share the case with a verified steward account, they can review and add notes."],
    ["How much does the service cost?","Free to try. Premium is $4.95 per month or $49.95 per year."],
  ];
  return (
    <PageWrap testid="page-faq" title="Frequently Asked Questions">
      <Accordion type="single" collapsible className="not-prose">
        {items.map(([q,a], i)=>(
          <AccordionItem key={i} value={`i${i}`} data-testid={`faq-item-${i}`}>
            <AccordionTrigger className="text-left font-display font-bold">{q}</AccordionTrigger>
            <AccordionContent className="text-slate-600">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </PageWrap>
  );
}

export function Terms() {
  return (
    <PageWrap testid="page-terms" title="Terms of Use" subtitle="Placeholder terms. Professional legal review recommended before production use.">
      <p><b>Acceptance of Terms.</b> By using FileMyGrievance.com you agree to these terms.</p>
      <p><b>No Legal Advice.</b> FileMyGrievance.com is not a law firm and does not provide legal advice. All content is for organizational and document-preparation purposes only.</p>
      <p><b>Accounts.</b> You are responsible for maintaining the security of your account.</p>
      <p><b>Content Ownership.</b> You retain ownership of the information you enter.</p>
      <p><b>Subscriptions.</b> Premium subscriptions are billed at $4.95/month or $49.95/year and may be canceled at any time.</p>
      <p><b>Termination.</b> We may suspend accounts that violate these terms.</p>
      <p><b>Limitation of Liability.</b> Use of this platform is at your own risk. No warranty is provided.</p>
      <p>[Additional professional review recommended.]</p>
    </PageWrap>
  );
}

export function Privacy() {
  return (
    <PageWrap testid="page-privacy" title="Privacy Policy" subtitle="What we collect, how we protect it, and how to delete it.">
      <p><b>Account Information.</b> We collect your name, email, and profile information you provide.</p>
      <p><b>Case Information.</b> Grievance data you enter is stored in an encrypted database associated with your account.</p>
      <p><b>Cookies.</b> We use cookies/localStorage for session management only.</p>
      <p><b>Subscriptions.</b> Payment processing is handled by Stripe. We do not store card details.</p>
      <p><b>Security.</b> Passwords are hashed. All API requests are authenticated. Case access is restricted to owners and users explicitly shared with.</p>
      <p><b>Deletion.</b> You may request full account and data deletion at any time by contacting us.</p>
    </PageWrap>
  );
}

export function Disclaimer() {
  return (
    <PageWrap testid="page-disclaimer" title="Disclaimer">
      <p>FileMyGrievance.com is an independent document-preparation and case-organization platform. It is not a union, law firm, or legal service. Information produced by the platform should be reviewed by the employee, union steward, business agent, or qualified legal professional. Use of the platform does not guarantee that a grievance will be filed, accepted, processed, or won.</p>
    </PageWrap>
  );
}

export function Contact() {
  const [form, setForm] = React.useState({name:"",email:"",subject:"",message:""});
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState("");
  const submit = async (e) => {
    e.preventDefault(); setError("");
    try {
      const { default: api } = await import("@/lib/api");
      await api.post("/contact", form);
      setSent(true);
    } catch (err) { setError(err.response?.data?.detail || "Failed to send"); }
  };
  return (
    <PageWrap testid="page-contact" title="Contact Us" subtitle="Questions, feedback, bug reports — we read every one.">
      {sent ? (
        <div className="not-prose bg-white border border-slate-200 p-8 rounded-md" data-testid="contact-success">
          <p className="text-slate-900 font-semibold">Thanks — your message was received.</p>
          <p className="text-sm text-slate-600 mt-1">We'll be in touch shortly.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="not-prose space-y-4 bg-white border border-slate-200 p-8 rounded-md">
          <input data-testid="contact-name" required placeholder="Name" className="w-full border border-slate-300 rounded-md px-3 py-2" value={form.name} onChange={e=>setForm({...form, name:e.target.value})}/>
          <input data-testid="contact-email" required type="email" placeholder="Email" className="w-full border border-slate-300 rounded-md px-3 py-2" value={form.email} onChange={e=>setForm({...form, email:e.target.value})}/>
          <input data-testid="contact-subject" required placeholder="Subject" className="w-full border border-slate-300 rounded-md px-3 py-2" value={form.subject} onChange={e=>setForm({...form, subject:e.target.value})}/>
          <textarea data-testid="contact-message" required placeholder="Message" rows={6} className="w-full border border-slate-300 rounded-md px-3 py-2" value={form.message} onChange={e=>setForm({...form, message:e.target.value})}/>
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <Button data-testid="contact-submit" type="submit" className="bg-red-600 hover:bg-red-700 text-white">Send message</Button>
        </form>
      )}
    </PageWrap>
  );
}
